import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import React, { useState, useEffect, useCallback, useTransition } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './StateSelector.css';
import { useGeoLocation } from '../contexts/GeoLocationContext';
import {
  COUNTRIES,
  getStatesForCountry,
  stateNameToSlug,
  STATE_CENTROIDS,
  CA_PROVINCE_CENTROIDS,
  PL_VOIVODESHIP_CENTROIDS,
  UK_NATION_CENTROIDS,
} from '../utils/stateUtils';

interface StateSelectorProps {
  showMessage?: boolean;
  asLink?: boolean;
  highlightLocation?: boolean;
  linkText?: string;
  billTitle?: string;
  country?: string;
  /** Fallback state name from URL param — used when GeoLocation context hasn't synced yet */
  stateName?: string;
}

// State/province centroids imported from stateUtils

const StateSelector: React.FC<StateSelectorProps> = ({
  showMessage = false,
  asLink = false,
  highlightLocation = false,
  linkText,
  billTitle: _billTitle,
  country: countryProp,
  stateName: stateNameProp,
}) => {
  const { location, setLocation } = useGeoLocation();
  const navigate = useNavigate();
  const { billSlug: billSlugParam, param } = useParams<{ billSlug?: string; param?: string }>();
  // Handle both :billSlug and :param (from BillRouter)
  const billSlug = billSlugParam || param;
  const [open, setOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>(countryProp || location?.country || 'united-states');
  const [selectedState, setSelectedState] = useState<string>('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  // Get filtered state/province list based on selected country
  const statesForCountry = getStatesForCountry(selectedCountry);
  const stateNames = statesForCountry.map((s) => s.name);

  // Dynamic region label per country convention
  const regionLabel =
    selectedCountry === 'canada'
      ? 'Province / Territory'
      : selectedCountry === 'poland'
        ? 'Voivodeship'
        : selectedCountry === 'united-kingdom'
          ? 'Nation'
          : 'State';

  useEffect(() => {
    // Pre-fill from saved location (T020)
    if (location?.country) setSelectedCountry(location.country);
    if (location?.state) setSelectedState(location.state);
    else setSelectedState('');
  }, [location?.state, location?.country]);

  // Get centroid for a state/province name
  const getCentroid = (stateName: string): { lat: number; lon: number } | undefined => {
    return (
      STATE_CENTROIDS[stateName] ||
      CA_PROVINCE_CENTROIDS[stateName] ||
      PL_VOIVODESHIP_CENTROIDS[stateName] ||
      UK_NATION_CENTROIDS[stateName]
    );
  };

  const handleStateSave = useCallback(() => {
    if (!selectedState) {
      setLocalError('Please select a state or province.');
      return;
    }
    const centroid = getCentroid(selectedState);
    if (!centroid) {
      setLocalError('Could not find location data for the selected region.');
      return;
    }

    startTransition(() => {
      setLocation({
        lat: centroid.lat,
        lon: centroid.lon,
        state: selectedState,
        country: selectedCountry,
      });
      setLocalError(null);
      setOpen(false);

      // If we're on a bill page, navigate to the country+state-scoped route
      if (billSlug) {
        const stateSlug = stateNameToSlug(selectedState);
        const targetPath = `/bill/${selectedCountry}/${stateSlug}/${billSlug}`;
        navigate(targetPath, { replace: true });
      }
    });
  }, [selectedState, selectedCountry, billSlug, navigate, setLocation, startTransition]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {showMessage && !location && (
        <Typography variant="h6" gutterBottom>
          Set your location to see the progress in your state
        </Typography>
      )}
      {asLink ? (
        <Button
          className={highlightLocation ? 'location-attention' : undefined}
          sx={
            highlightLocation
              ? (theme) => ({
                  '--location-glow-light': theme.palette.primary.light,
                  '--location-glow-main': theme.palette.primary.main,
                  '--location-glow-dark': theme.palette.primary.dark,
                  backgroundColor: theme.palette.background.paper,
                })
              : undefined
          }
          onClick={() => {
            setOpen(true);
            setLocalError(null);
            if (location?.state) setSelectedState(location.state);
          }}
          variant="outlined"
          aria-label={
            location?.state || stateNameProp
              ? `View ${location?.state || stateNameProp} bill status`
              : 'Set location for bill status'
          }
        >
          <LocationOnIcon sx={{ fontSize: '1.2rem' }} />
          {linkText ||
            (location?.state || stateNameProp
              ? `${location?.state || stateNameProp} Bill Status`
              : 'Set Location for Bill Status')}
        </Button>
      ) : (
        <Button
          variant="outlined"
          startIcon={<LocationOnIcon />}
          onClick={() => {
            setOpen(true);
            setLocalError(null);
            if (location?.state) setSelectedState(location.state);
            else setSelectedState('');
          }}
        >
          {location?.state || location?.zip || 'Set your location'}
        </Button>
      )}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setLocalError(null);
        }}
        aria-labelledby="set-location-title"
        aria-describedby="set-location-description"
      >
        <DialogTitle id="set-location-title">Select Your Location</DialogTitle>
        <DialogContent>
          <Typography id="set-location-description" variant="body2" sx={{ mb: 2 }} color="text.secondary">
            Choose your country and {regionLabel.toLowerCase()} to see region-specific bill progress.
          </Typography>
          {localError && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              {localError}
            </Alert>
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel id="country-select-label">Country</InputLabel>
            <Select
              labelId="country-select-label"
              id="country-select"
              value={selectedCountry}
              label="Country"
              onChange={(e) => {
                const newCountry = e.target.value as string;
                setSelectedCountry(newCountry);
                setSelectedState(''); // Clear state when country changes (FR-015)
                if (localError) setLocalError(null);
              }}
            >
              {COUNTRIES.map((c) => (
                <MenuItem key={c.slug} value={c.slug}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="state-select-label">{regionLabel}</InputLabel>
            <Select
              labelId="state-select-label"
              id="state-select"
              value={selectedState}
              label={regionLabel}
              onChange={(e) => {
                setSelectedState(e.target.value as string);
                if (localError) setLocalError(null);
              }}
            >
              {stateNames.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          {location && (
            <Box sx={{ flexGrow: 1, display: 'flex' }}>
              <Button
                color="secondary"
                onClick={() => {
                  // Clear localStorage first
                  try {
                    localStorage.removeItem('userLocation');
                  } catch {}

                  // Clear location state
                  setLocation(null);
                  setSelectedState('');
                  setSelectedCountry('united-states');
                  setLocalError(null);
                  setOpen(false);

                  // Navigate to bill-only route to show BillStateDirectory
                  if (billSlug) {
                    navigate(`/bill/${billSlug}`, { replace: true });
                  } else if (window.location.pathname.match(/\/bill\//)) {
                    // Fallback: parse from pathname if billSlug param isn't available
                    const segments = window.location.pathname.split('/').filter(Boolean);
                    const billIndex = segments.indexOf('bill');
                    if (billIndex !== -1) {
                      const remainder = segments.slice(billIndex + 1);
                      // With country-aware URLs, the bill slug is the last segment
                      const currentBillSlug = remainder[remainder.length - 1];
                      navigate(`/bill/${currentBillSlug}`, { replace: true });
                    }
                  }
                }}
              >
                Unassign Location
              </Button>
            </Box>
          )}
          <Button
            onClick={() => {
              setOpen(false);
              setLocalError(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleStateSave} variant="contained" disabled={!selectedState}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StateSelector;
